// Hours & pausing (docs/PHASE7-CONTRACT.md "Partner Hub"; ShopHoursSpec in contract.d.ts).
// The weekly hours editor's draft, its validation back to a ShopHoursSpec, and the pause wording.
// Pure: no DOM, no Intl. The open/closed truth is the server's (ShopHours.openNow); pauseState is
// the shared money.js rule, used here only so a pause that ends on screen reads right before the
// next refetch.

import { pauseState } from '@popcart/core/v1';

/** @typedef {import('@popcart/core/v1').ShopHours} ShopHours */
/** @typedef {import('@popcart/core/v1').ShopHoursSpec} ShopHoursSpec */
/** @typedef {'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'} DayKey */
/** @typedef {{ open: string; close: string }} WindowDraft */
/** @typedef {{ closed: boolean; windows: WindowDraft[] }} DayDraft */
/**
 * @typedef {object} HoursDraft
 * @property {'daily' | 'perDay' | 'always'} mode  same hours every day / per day / no hours set (always open)
 * @property {WindowDraft} daily
 * @property {Record<DayKey, DayDraft>} days
 */

/** @type {{ key: DayKey; label: string; short: string }[]} */
export const DAYS = [
  { key: 'mon', label: 'Monday', short: 'Mon' },
  { key: 'tue', label: 'Tuesday', short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday', short: 'Thu' },
  { key: 'fri', label: 'Friday', short: 'Fri' },
  { key: 'sat', label: 'Saturday', short: 'Sat' },
  { key: 'sun', label: 'Sunday', short: 'Sun' },
];

export const MAX_WINDOWS = 2;
export const DEFAULT_WINDOW = /** @type {WindowDraft} */ ({ open: '06:00', close: '21:00' });

/** The pause choices the API takes (pauseMinutes), with their button labels. */
export const PAUSE_OPTIONS = /** @type {const} */ ([
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
]);

const HM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** @param {unknown} w @returns {w is [string, string]} */
function isPair(w) {
  return Array.isArray(w) && w.length === 2 && typeof w[0] === 'string' && typeof w[1] === 'string';
}

/** @param {[string, string]} w @returns {WindowDraft} */
const toWin = (w) => ({ open: w[0], close: w[1] });

/**
 * Server spec → editor draft. {} = always open; { daily } = same hours every day; anything else is
 * per day (a missing day is closed, as the API reads it).
 * @param {ShopHoursSpec | null | undefined} spec
 * @returns {HoursDraft}
 */
export function toDraft(spec) {
  const s = /** @type {Record<string, unknown>} */ (spec && typeof spec === 'object' && !Array.isArray(spec) ? spec : {});
  const keys = Object.keys(s);
  const daily = isPair(s.daily) ? toWin(s.daily) : { ...DEFAULT_WINDOW };
  /** @type {Record<DayKey, DayDraft>} */
  const days = /** @type {any} */ ({});
  for (const d of DAYS) {
    const raw = s[d.key];
    /** @type {WindowDraft[]} */
    let windows = [];
    if (isPair(raw)) windows = [toWin(raw)];
    else if (Array.isArray(raw)) windows = raw.filter(isPair).slice(0, MAX_WINDOWS).map(toWin);
    else if (keys.includes('daily') && isPair(s.daily)) windows = [toWin(s.daily)];
    days[d.key] = windows.length ? { closed: false, windows } : { closed: !!keys.length && !keys.includes('daily'), windows: [{ ...daily }] };
  }
  /** @type {HoursDraft['mode']} */
  const mode = !keys.length ? 'always' : keys.includes('daily') ? 'daily' : 'perDay';
  return { mode, daily, days };
}

/** "06:00" → minutes past midnight, or null. @param {string} s */
function mins(s) {
  const m = HM_RE.exec(String(s));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * One window's problem, or null. A window may cross midnight ("18:00" → "02:00").
 * @param {WindowDraft} w
 */
export function windowProblem(w) {
  if (mins(w.open) === null || mins(w.close) === null) return 'Pick an opening and a closing time.';
  if (w.open === w.close) return 'Opening and closing can’t be the same time.';
  return null;
}

/** Two windows on one day may not overlap. @param {WindowDraft} a @param {WindowDraft} b */
function overlaps(a, b) {
  /** @param {WindowDraft} w @returns {[number, number][]} */
  const span = (w) => {
    const s = /** @type {number} */ (mins(w.open));
    const e = /** @type {number} */ (mins(w.close));
    return e > s ? [[s, e]] : [[s, 1440], [0, e]];
  };
  return span(a).some(([s1, e1]) => span(b).some(([s2, e2]) => s1 < e2 && s2 < e1));
}

/**
 * Draft → the spec the API takes, or the problems by field ("daily", "mon.0", "mon").
 * @param {HoursDraft} d
 * @returns {{ ok: true; spec: ShopHoursSpec } | { ok: false; errors: Record<string, string> }}
 */
export function fromDraft(d) {
  /** @type {Record<string, string>} */
  const errors = {};
  if (d.mode === 'always') return { ok: true, spec: {} };
  if (d.mode === 'daily') {
    const p = windowProblem(d.daily);
    if (p) return { ok: false, errors: { daily: p } };
    return { ok: true, spec: { daily: [d.daily.open, d.daily.close] } };
  }
  /** @type {Record<string, [string, string][]>} */
  const spec = {};
  let anyOpen = false;
  for (const { key } of DAYS) {
    const day = d.days[key];
    if (!day || day.closed) {
      spec[key] = [];
      continue;
    }
    if (!day.windows.length) {
      errors[key] = 'Add opening hours, or mark the day closed.';
      continue;
    }
    if (day.windows.length > MAX_WINDOWS) errors[key] = 'Up to 2 time windows a day.';
    day.windows.forEach((w, i) => {
      const p = windowProblem(w);
      if (p) errors[`${key}.${i}`] = p;
    });
    const [a, b] = day.windows;
    if (a && b && !windowProblem(a) && !windowProblem(b) && overlaps(a, b)) errors[`${key}.1`] = 'The second window overlaps the first.';
    spec[key] = day.windows.slice(0, MAX_WINDOWS).map((w) => /** @type {[string, string]} */ ([w.open, w.close]));
    anyOpen = true;
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  if (!anyOpen) return { ok: false, errors: { days: 'Open at least one day, or switch Taking orders off instead.' } };
  return { ok: true, spec: /** @type {ShopHoursSpec} */ (spec) };
}

/** Did the draft change from the saved spec? @param {HoursDraft} d @param {ShopHoursSpec} saved */
export function hoursDirty(d, saved) {
  const r = fromDraft(d);
  if (!r.ok) return true;
  return canonical(r.spec) !== canonical(saved);
}

/** @param {ShopHoursSpec} spec */
function canonical(spec) {
  const s = /** @type {Record<string, unknown>} */ (spec || {});
  if (!Object.keys(s).length) return '{}';
  if (isPair(s.daily)) return `daily:${s.daily.join('-')}`;
  return DAYS.map(({ key }) => {
    const raw = s[key];
    const list = isPair(raw) ? [raw] : Array.isArray(raw) ? raw.filter(isPair) : [];
    return `${key}:${list.map((w) => w.join('-')).join(',')}`;
  }).join('|');
}

/**
 * "3:30 PM" in the tablet's local time (the shops are in Manila).
 * @param {string | Date} iso
 */
export function clock12(iso) {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "06:00" → "6:00 AM"; "24:00" or "00:00" → "midnight" when closing. @param {string} hm */
export function wall12(hm) {
  const m = mins(hm);
  if (m === null) return hm;
  const h = Math.floor(m / 60);
  return `${h % 12 || 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** One line for the saved hours ("Every day · 6:00 AM – 9:00 PM"). @param {ShopHoursSpec} spec */
export function hoursSummary(spec) {
  const d = toDraft(spec);
  if (d.mode === 'always') return 'Open around the clock (no hours set)';
  if (d.mode === 'daily') return `Every day · ${wall12(d.daily.open)} – ${wall12(d.daily.close)}`;
  const open = DAYS.filter((x) => !d.days[x.key].closed);
  if (!open.length) return 'Closed every day';
  return `Open ${open.length} ${open.length === 1 ? 'day' : 'days'} a week · hours per day`;
}

/**
 * The status card: open or closed now, and whether customers can order.
 * @param {ShopHours} h
 * @param {number} now  ms
 * @returns {{ title: 'Open now' | 'Closed now'; open: boolean; taking: boolean; reason: 'off' | 'paused' | 'hours' | null; detail: string; pausedUntil: string | null }}
 */
export function statusView(h, now) {
  const p = pauseState({ acceptingOrders: h.acceptingOrders, pausedUntil: h.pausedUntil, now: new Date(now) });
  const open = h.openNow;
  if (p.reason === 'off') return { title: open ? 'Open now' : 'Closed now', open, taking: false, reason: 'off', detail: 'Not taking orders. Customers see you as closed.', pausedUntil: null };
  if (p.reason === 'paused' && p.until) return { title: open ? 'Open now' : 'Closed now', open, taking: false, reason: 'paused', detail: pausedText(p.until), pausedUntil: p.until };
  if (!open) return { title: 'Closed now', open, taking: false, reason: 'hours', detail: 'Outside your hours. Orders open again at your next opening time.', pausedUntil: null };
  return { title: 'Open now', open, taking: true, reason: null, detail: 'Taking orders.', pausedUntil: null };
}

/** "Paused until 3:30 PM". @param {string} until */
export function pausedText(until) {
  return `Paused until ${clock12(until)}`;
}

/** "Paused for 30 min. Back at 3:30 PM." after a pause lands. @param {number} minutes @param {string | null} until */
export function pausedToast(minutes, until) {
  const opt = PAUSE_OPTIONS.find((o) => o.minutes === minutes);
  return `Paused for ${opt ? opt.label : `${minutes} min`}.${until ? ` Back at ${clock12(until)}.` : ''}`;
}

/**
 * The client calls the page makes, one place (tests pass a fake api).
 * @param {Pick<import('@popcart/core/v1').PopCartApi, 'updateShopHours'>} api
 * @param {string} shopId
 */
export function hoursActions(api, shopId) {
  return {
    /** @param {ShopHoursSpec} hours */
    saveHours: (hours) => api.updateShopHours(shopId, { hours }),
    /** @param {boolean} on */
    setTaking: (on) => api.updateShopHours(shopId, { acceptingOrders: on }),
    /** @param {15 | 30 | 60 | 120} minutes */
    pause: (minutes) => api.updateShopHours(shopId, { pauseMinutes: minutes }),
    resume: () => api.updateShopHours(shopId, { pauseMinutes: 0 }),
  };
}
